import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import type { FC } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  ViewportPortal,
  useNodesState,
  useStoreApi,
} from "@xyflow/react";
import type { NodeTypes, EdgeTypes } from "@xyflow/react";
import { GrayColors } from "@smartsuite/types";
import { TriggerNode } from "./nodes/TriggerNode";
import { ActionNode } from "./nodes/ActionNode";
import { BranchesBlockNode } from "./nodes/BranchesBlockNode";
import { BranchHeaderNode } from "./nodes/BranchHeaderNode";
import { LoopGroupNode } from "./nodes/LoopGroupNode";
import { AddActionNode } from "./nodes/AddActionNode";
import { SectionLabelNode } from "./nodes/SectionLabelNode";
import { StepEdge } from "./edges/StepEdge";
import { BranchFanEdge } from "./edges/BranchFanEdge";
import {
  FlowCanvasHandlersContext,
  FlowCanvasMenuDismissContext,
} from "./nodes/context";
import type { FlowCanvasHandlers } from "./nodes/context";
import { FlowCanvasGlobalStyles } from "./global-styles";
import { FlowZoomControl } from "./FlowZoomControl";
import { FlowViewportInit } from "./FlowViewportInit";
import { StyledFlowCanvasShell } from "./style";
import { useFlowGraph } from "./hooks/use-flow-graph";
import { useFlowLayout } from "./hooks/use-flow-layout";
import { useNodeReorder } from "./hooks/use-node-reorder";
import { FLOW_MIN_ZOOM, FLOW_MAX_ZOOM } from "./graph/constants";
import { syncGraphNodes } from "./graph/sync-nodes";
import type { FlowNode } from "./graph/types";
import { useRootStore } from "../../stores/hooks";

const nodeTypes: NodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  branchesBlock: BranchesBlockNode,
  branchHeader: BranchHeaderNode,
  loopGroup: LoopGroupNode,
  addAction: AddActionNode,
  sectionLabel: SectionLabelNode,
};

const edgeTypes: EdgeTypes = {
  step: StepEdge,
  branchFan: BranchFanEdge,
};

const FlowCanvasInner = observer(() => {
  const store = useRootStore();
  const graph = useFlowGraph();
  const { geometries, allMeasured } = useFlowLayout(graph.layout);
  const {
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    dropIndicatorX,
    dropIndicatorY,
    dropIndicatorWidth,
  } = useNodeReorder();

  // Controlled RF node state: React Flow v12 reports node measurements as
  // dimension changes through onNodesChange. Feeding it a fully re-derived
  // `nodes` array with no change handler dropped those measurements and reset
  // RF's nodeLookup on every sync, so nodes never left visibility:hidden
  // (Task 10 QA bug). Structural/layout updates are synced into this state by
  // the effect below, preserving `measured` on surviving nodes.
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState<FlowNode>([]);
  const storeApi = useStoreApi();
  const shellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRfNodes((prev) => {
      // Read the freshest measurements straight from RF's internal nodeLookup
      // AT FLUSH TIME: internals are written synchronously when a node is
      // measured, while the matching dimension change reaches `prev` (our
      // controlled state) only on a later flush. Resubmitting a node without
      // `measured` inside that window makes React Flow reset its handleBounds
      // for good — the node's edges then silently never render (this is what
      // hid the branch-fan connectors in QA round 2).
      const liveMeasured = new Map(
        [...storeApi.getState().nodeLookup].map(([id, internal]) => [
          id,
          internal.measured,
        ]),
      );
      return syncGraphNodes(prev, graph.nodes, geometries, liveMeasured);
    });
  }, [graph.nodes, geometries, setRfNodes, storeApi]);

  // One-way reveal latch: the canvas stays hidden only until the FIRST fully
  // measured layout. Later structural edits add unmeasured nodes for a frame;
  // collapsing the whole canvas back to opacity 0 would blink it.
  const revealedRef = useRef(false);
  useEffect(() => {
    if (allMeasured) revealedRef.current = true;
  }, [allMeasured]);
  const revealed = revealedRef.current || allMeasured;

  return (
    <FlowCanvasMenuDismissContext.Provider
      value={store.flowCanvas.menuDismissSignal}
    >
      <StyledFlowCanvasShell ref={shellRef}>
        <ReactFlow
          nodes={rfNodes}
          onNodesChange={onNodesChange}
          edges={graph.edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          style={{ opacity: revealed ? 1 : 0 }}
          minZoom={FLOW_MIN_ZOOM}
          maxZoom={FLOW_MAX_ZOOM}
          // legacy behavior: wheel pans, ctrl/meta+wheel zooms
          panOnScroll
          zoomOnScroll={false}
          zoomOnPinch
          // per-node `draggable` (set by build-graph for eligible action cards)
          // overrides this global default, so only those cards can be reordered
          nodesDraggable={false}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onPaneClick={() => store.flowCanvas.dismissOpenMenus()}
          nodesConnectable={false}
          elementsSelectable={false}
          onlyRenderVisibleElements
          proOptions={{ hideAttribution: true }}
        >
          <FlowViewportInit
            shellRef={shellRef}
            ready={revealed}
            viewportResetKey={store.flowCanvas.viewportResetKey}
          />
          {dropIndicatorY !== null && (
            <ViewportPortal>
              <div
                style={{
                  position: "absolute",
                  transform: `translate(${
                    dropIndicatorX ?? 0
                  }px, ${dropIndicatorY}px)`,
                  width: dropIndicatorWidth ?? 0,
                  height: 2,
                  background: GrayColors["G-4"],
                  pointerEvents: "none",
                }}
              />
            </ViewportPortal>
          )}
          <FlowZoomControl />
        </ReactFlow>
      </StyledFlowCanvasShell>
    </FlowCanvasMenuDismissContext.Provider>
  );
});
FlowCanvasInner.displayName = "FlowCanvasInner";

// eslint-disable-next-line import/no-unused-modules -- consumed by AutomationForm (Task 8)
export type FlowCanvasProps = FlowCanvasHandlers;

export const FlowCanvas: FC<FlowCanvasProps> = (handlers) => (
  <ReactFlowProvider>
    <FlowCanvasGlobalStyles />
    <FlowCanvasHandlersContext.Provider value={handlers}>
      <FlowCanvasInner />
    </FlowCanvasHandlersContext.Provider>
  </ReactFlowProvider>
);
